import { Injectable } from '@angular/core';
import { getAngularJSService } from '../ng-upgrade.utils';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface MdDialogConfirm {
  title(value: string): MdDialogConfirm;
  textContent(value: string): MdDialogConfirm;
  ok(value: string): MdDialogConfirm;
  cancel(value: string): MdDialogConfirm;
}

interface MdDialog {
  confirm(): MdDialogConfirm;
  show<T = unknown>(options: MdDialogConfirm | Record<string, unknown>): Promise<T>;
  hide<T = unknown>(result?: T): void;
}

interface MdDialogLocalsScope {
  $dialogLocals: Record<string, unknown>;
}

const SAFE_SELECTOR = /^[a-z][a-z0-9]*(-[a-z0-9]+)+$/;

const SAFE_LOCAL_KEY = /^[a-z][A-Za-z0-9]*$/;

function toKebabCase(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  // WRAPPER — substituir por MatDialog na Fase de Saída (FS.4)

  confirm(options: ConfirmOptions): Promise<boolean> {
    const $mdDialog = getAngularJSService<MdDialog>('$mdDialog');
    if (!$mdDialog) {
      return Promise.resolve(false);
    }

    const dialog = $mdDialog
      .confirm()
      .title(options.title)
      .textContent(options.message)
      .ok(options.confirmLabel ?? 'Confirmar')
      .cancel(options.cancelLabel ?? 'Cancelar');

    return $mdDialog.show(dialog).then(
      () => true,
      () => false
    );
  }

  open<T>(componentSelector: string, locals?: Record<string, unknown>): Promise<T | undefined> {
    const $mdDialog = getAngularJSService<MdDialog>('$mdDialog');
    if (!$mdDialog) {
      return Promise.resolve(undefined);
    }

    if (!SAFE_SELECTOR.test(componentSelector)) {
      console.error(`[DialogService] Seletor de componente inválido: "${componentSelector}".`);
      return Promise.resolve(undefined);
    }

    const scopeLocals = locals ?? {};
    const bindings = Object.keys(scopeLocals)
      .filter((key) => {
        if (SAFE_LOCAL_KEY.test(key)) return true;
        console.warn(`[DialogService] Local ignorado (não é um @Input em camelCase): "${key}".`);
        return false;
      })
      .map((key) => ` [${toKebabCase(key)}]="$dialogLocals.${key}"`)
      .join('');

    return $mdDialog
      .show<T>({
        template: `<md-dialog><${componentSelector}${bindings}></${componentSelector}></md-dialog>`,
        controller: [
          '$scope',
          function ($scope: MdDialogLocalsScope): void {
            $scope.$dialogLocals = scopeLocals;
          },
        ],
        locals,
        clickOutsideToClose: true,
      })
      .then(
        (result: T) => result,
        () => undefined
      );
  }

  close<T>(result?: T): void {
    const $mdDialog = getAngularJSService<MdDialog>('$mdDialog');
    $mdDialog?.hide(result);
  }
}
